import { IonHeader, IonToolbar, IonButtons, IonButton, IonTitle, IonContent, IonList, IonItem, IonLabel, IonPage, IonIcon } from "@ionic/react";
import { checkmark, close, globeOutline, phonePortraitOutline } from "ionicons/icons";
import { useDeviceQuery } from "../query/deviceQuery";
import { useDeviceLocaleMutation } from "../query/deviceLocaleMutation";
import useUX from '../utils/ux';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import { DEVICE_LANGUAGES, deviceLanguageOf, deviceLocaleForLanguage, type DeviceLanguage } from '../utils/deviceLocale';
import styles from './DeviceDisplayModal.module.css';

const LanguageRow = ({ title, note, tag, icon, selected, disabled, onSelect }: {
    title: string,
    note?: string,
    tag?: string,
    icon?: string,
    selected: boolean,
    disabled: boolean,
    onSelect: () => void,
}) => (
    <IonItem button detail={false} disabled={disabled} onClick={onSelect}
        className={selected ? styles.rowSelected : undefined}>
        {icon && (
            <span slot="start" className={`${styles.rowIcon} ${styles.rowIconAccent}`} aria-hidden="true">
                <IonIcon icon={icon} />
            </span>
        )}
        <IonLabel>
            <h2 className={styles.rowTitle}>{title}</h2>
            {note && <p>{note}</p>}
        </IonLabel>
        {tag && <span slot="end" className={styles.rowTag}>{tag}</span>}
        {selected && <IonIcon slot="end" color="primary" icon={checkmark} className={styles.rowCheck} />}
    </IonItem>
);

/**
 * Picks the language the device displays in. Rows are the app's languages;
 * the phone's current app language is offered as the suggestion whenever the
 * device isn't already on it. Tapping a row saves it.
 */
const DeviceLocaleModal = ({ dismiss, deviceId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceId: string }) => {
    const { t, i18n } = useModalTranslation();
    const { t: tCommon } = useCommonTranslation();
    const deviceQuery = useDeviceQuery(deviceId);
    const mutation = useDeviceLocaleMutation(deviceId);
    const { presentResult } = useUX();

    const deviceLocale: string | null = deviceQuery.data?.locale ?? null;
    const deviceLanguage = deviceLanguageOf(deviceLocale);
    const appLanguage = deviceLanguageOf(i18n.resolvedLanguage ?? i18n.language);
    const suggestedLanguage = appLanguage && appLanguage !== deviceLanguage ? appLanguage : undefined;

    const saveLocale = async (locale: string | null) => {
        if (locale === deviceLocale) {
            dismiss(null, 'cancel');
            return;
        }
        presentResult(async () => {
            await mutation.mutateAsync(locale);
            dismiss(locale, 'select');
        }, t('device_locale.updated'));
    };

    const languageRow = (language: DeviceLanguage, extra?: { icon?: string, note?: string }) => {
        const locale = deviceLocaleForLanguage(language);
        return (
            <LanguageRow key={language}
                title={tCommon(`language_names.${language}`)}
                tag={locale}
                icon={extra?.icon}
                note={extra?.note}
                selected={deviceLanguage === language}
                disabled={mutation.isPending}
                onSelect={() => saveLocale(locale)} />
        );
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{t('device_locale.title')}</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={() => dismiss(null, 'cancel')}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent color="light">
                <div className={styles.wrapper}>
                    {deviceLocale && !deviceLanguage && (
                        // A tag the app can't name (set by support or an older app): keep it visible and selected.
                        <section className={styles.section}>
                            <h3 className={styles.sectionLabel}>{t('device_locale.section_current')}</h3>
                            <IonList className={styles.listCard}>
                                <LanguageRow title={deviceLocale} selected disabled={mutation.isPending}
                                    onSelect={() => saveLocale(deviceLocale)} />
                            </IonList>
                        </section>
                    )}
                    {suggestedLanguage && (
                        <section className={styles.section}>
                            <h3 className={styles.sectionLabel}>{t('device_locale.section_suggested')}</h3>
                            <IonList className={styles.listCard}>
                                {languageRow(suggestedLanguage, { icon: phonePortraitOutline, note: t('device_locale.suggested_note') })}
                            </IonList>
                        </section>
                    )}
                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>{t('device_locale.section_languages')}</h3>
                        <IonList className={styles.listCard}>
                            <LanguageRow
                                icon={globeOutline}
                                title={t('device_locale.default_label')}
                                note={t('device_locale.default_note')}
                                selected={deviceLocale === null}
                                disabled={mutation.isPending}
                                onSelect={() => saveLocale(null)} />
                            {DEVICE_LANGUAGES.map((language) => languageRow(language))}
                        </IonList>
                        <p className={styles.note}>{t('device_locale.note')}</p>
                    </section>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default DeviceLocaleModal;
